/**
 * The Flow Segment Data service provides information about the speeds and travel times of the road
 * fragment closest to the given coordinates.
 *
 * @class TrafficFlowSegmentData
 * @module Services
 * @uses Chain
 * @uses KeyMixin
 * @param {Object} options Options to be passed to the flowSegmentData call.
 * @param {String} options.point Coordinates of the point close to the road segment.
 * @param {String} options.key Traffic Flow API key that can be obtained from
 *     <a href="http://developer.tomtom.com">TomTom's developer portal</a>.
 * @param {String} options.style The type of flow data to be displayed on the tile. Available options: absolute,
 *     relative, relative-delay.
 * @param {Number} options.zoom Zoom level. This will affect traffic incident coordinates to present both
 *     road sides separately.
 * @param {String} [options.unit=KMPH] Unit of speed. Available options: KMPH, MPH.
 * @param {Number} [options.thickness=10] Segment width multiplier. Accepts values in range from 0 to 20.
 * @param {Function} [options.fail] The callback function to be called on failure.
 * @param {Function} callback The callback function to be called on success.
 * @constructor
 */
define(['./validators', 'rest/flowSegmentData', './chain', 'common/parameterApplications'],
    function(validators, restFlowSegmentData, chain, parameterApplications) {

        var fields = {
            key: {validator: validators.string},

            /**
         * @method zoom
         * @chainable
         * @param {Integer} zoom Zoom level. This will affect traffic incident coordinates to present both road
         * sides separately. Should be in range from 0 to 18.
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {String} The same service instance or the current option value if no argument was given
         */
            zoom: {validator: validators.numberInInterval(0, 18), required: true,
                   application: parameterApplications.PATH},

            /**
         * @method style
         * @chainable
         * @param {String} style The type of flow data to be displayed on the tile. Available options: absolute,
         * relative, relative-delay.
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {String} The same service instance or the current option value if no argument was given
         */
            style: {validator: validators.oneOfValue(['absolute', 'relative', 'relative-delay'],
                'traffic flow style'), required: true, application: parameterApplications.PATH},

            /**
         * @method point
         * @chainable
         * @param {String} point Coordinates of the point close to the road segment.
         * They have to be comma-separated and calculated using EPSG4326 projection.
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {String} The same service instance or the current option value if no argument was given
         */
            point: {validator: validators.point, required: true},

            /**
         * @method unit
         * @chainable
         * @param {String} unit Unit of speed. Avilable values: KMPH, MPH.
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {String} The same service instance or the current option value if no argument was given
         */
            unit: {validator: validators.oneOfValue(['KMPH', 'MPH'])},

            /**
         * @method thickness
         * @chainable
         * @param {Integer} thickness Segment width multiplier. Value should be in range from 1 to 20.
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {String} The same service instance or the current option value if no argument was given
         */
            thickness: {validator: validators.numberInInterval(1, 20)},

            /**
         * @method openLr
         * @chainable
         * @param {Boolean} openLr Specifies if response include OpenLR code.
         * @throws {TypeError} If the given argument cannot be converted into a valid value
         * @return {String} The same service instance or the current option value if no argument was given
         */
            openLr: {validator: validators.bool}
        };

        function handleChain(options, callback) {
            return restFlowSegmentData.flowSegmentData(fields, options, callback);
        }

        return function(options) {
            options = options || {};
            options.__serviceType = 'traffic';
            return chain(fields, handleChain, options);
        };
    });



// WEBPACK FOOTER //
// ./services/trafficFlowSegmentData.js